import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Spinner, Table, ServiceAreaPill, ClampText } from '@/components/ui';
import { Link } from 'react-router-dom';
import ServerPagination from '../../../components/ServerPagination';
import { getScheduleTasks, getScheduleVehicles, type ScheduleTaskForChart, type ScheduleVehicle } from '../../../services/scheduleService';
import { getScheduleTableDateRange } from '../utils/scheduleTableDateRange';
import {
  normalizeScheduleFilterValues,
  taskMatchesScheduleActivityFilter,
  toScheduleListParam,
  type ScheduleResourcePageFilters,
} from '../utils/scheduleResourceAggregation';
import { taskMatchesSchedulePlannedDateFilters } from '../utils/schedulePlannedDateWindow';
import { orderScheduleTasksForDisplay } from '../utils/scheduleTaskDependencyOrder';
import { compareLocale, compareNullableTime, dateStringToSortTime } from '../utils/scheduleTableSort';
import { formatScheduleTaskStatusLabel, taskStatusMatchesScheduleSelection } from '../utils/scheduleTaskStatuses';
import { formatDate } from '@/utils/displayDateTime';

export interface ScheduleTaskTableProps {
  filters: ScheduleResourcePageFilters;
}

interface TaskRow {
  taskId: number;
  foremanAreaName: string;
  serviceAreaName: string;
  projectLabel: string;
  taskNumber: string;
  taskName: string;
  assigneesLabel: string;
  plannedStartRaw: string;
  plannedEndRaw: string;
  status: string;
  yearRound: boolean;
}

type TaskSortKey =
  | 'scheduleOrder'
  | 'foremanAreaName'
  | 'serviceAreaName'
  | 'taskNumber'
  | 'taskName'
  | 'assigneesLabel'
  | 'plannedStart'
  | 'plannedEnd'
  | 'status'
  | 'yearRound';

function formatScheduleDateCell(iso: string | null | undefined): string {
  if (!iso || !String(iso).trim()) return '–';
  const raw = String(iso).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return formatDate(d);
  }
  const t = Date.parse(raw);
  return Number.isFinite(t) ? formatDate(t) : raw;
}

function assigneesLabel(assignees: ScheduleTaskForChart['assignees'] | undefined): string {
  const list = Array.isArray(assignees) ? assignees : [];
  if (list.length === 0) return '–';
  return list
    .map((a) => `${String(a.name || '').trim()} ${String(a.lastName || '').trim()}`.trim() || `#${a.id}`)
    .join('; ');
}

function hasPlannedStartAndEnd(task: ScheduleTaskForChart): boolean {
  if (task.yearRoundSchedule) return true;
  const plannedStartRaw = String(task.identifiedDate ?? task.startDate ?? '').trim();
  const plannedEndRaw = String(task.plannedCompletionDate ?? '').trim();
  return plannedStartRaw.length > 0 && plannedEndRaw.length > 0;
}

/**
 * Match equipment type filter using unit → vehicle type mapping (same idea as resource Gantt).
 * A task is kept when any of its units maps to any of the selected types; empty = no filter.
 */
function filterTasksByEquipmentTypeMapped(
  tasks: ScheduleTaskForChart[],
  vehicleMeta: ScheduleVehicle[] | null,
  equipmentTypeFilter: string[]
): ScheduleTaskForChart[] {
  const wanted = new Set(normalizeScheduleFilterValues(equipmentTypeFilter).map((t) => t.toLowerCase()));
  if (wanted.size === 0) return tasks;
  const unitToType = new Map<string, string>();
  (Array.isArray(vehicleMeta) ? vehicleMeta : []).forEach((v) => {
    const unit = (v.unitNumber || '').trim();
    if (!unit) return;
    const typ = (v.vehicleType || 'Unspecified').trim() || 'Unspecified';
    unitToType.set(unit, typ);
  });
  return tasks.filter((task) => {
    const vehicles = Array.isArray(task.vehicles) ? task.vehicles : [];
    return vehicles.some((vv) => {
      const unit = (vv.unitUsed || '').trim();
      if (!unit) return false;
      const mapped = (unitToType.get(unit) || 'Unspecified').toLowerCase();
      return wanted.has(mapped);
    });
  });
}

const ScheduleTaskTable: React.FC<ScheduleTaskTableProps> = ({ filters }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<'forbidden' | 'error' | null>(null);
  const [tasksRaw, setTasksRaw] = useState<ScheduleTaskForChart[] | null>(null);
  const [sortKey, setSortKey] = useState<TaskSortKey>('scheduleOrder');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { dateFrom, dateTo } = useMemo(
    () => getScheduleTableDateRange(filters.startDate, filters.endDate),
    [filters.startDate, filters.endDate]
  );

  /**
   * Wire form of the multiselect area/work-type filters (comma-separated, `undefined` when empty).
   * `filters` is the page's *applied* state, so these arrays only change on Apply — safe deps.
   */
  const serviceAreaParam = toScheduleListParam(filters.serviceAreas);
  const foremanAreaParam = toScheduleListParam(filters.foremanAreas);
  const workTypeParam = toScheduleListParam(filters.workTypes);

  useEffect(() => {
    setPage(1);
  }, [
    dateFrom,
    dateTo,
    filters.serviceAreas,
    filters.foremanAreas,
    filters.workTypes,
    filters.taskSearch,
    filters.projectIds,
    filters.activityIds,
    filters.activityNumbers,
    filters.statuses,
    filters.equipmentTypes,
    filters.startDate,
    filters.endDate,
  ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setTasksRaw(null);

    const tasksPromise = getScheduleTasks({
      dateFrom,
      dateTo,
      serviceArea: serviceAreaParam,
      foremanArea: foremanAreaParam,
      workType: workTypeParam,
      taskSearch: filters.taskSearch || undefined,
      statuses: filters.statuses || undefined,
    });

    const equipmentTypeNeedles = normalizeScheduleFilterValues(filters.equipmentTypes);
    const vehiclesPromise =
      equipmentTypeNeedles.length > 0
        ? getScheduleVehicles({
            serviceArea: serviceAreaParam,
            foremanArea: foremanAreaParam,
          })
        : Promise.resolve([] as ScheduleVehicle[]);

    Promise.allSettled([tasksPromise, vehiclesPromise]).then(([tasksResult, vehiclesResult]) => {
      if (cancelled) return;
      const vehicleRows =
        vehiclesResult.status === 'fulfilled' && Array.isArray(vehiclesResult.value) ? vehiclesResult.value : [];
      if (vehiclesResult.status === 'rejected') {
        console.error('Schedule task table: failed to load vehicles', vehiclesResult.reason);
      }

      if (tasksResult.status === 'fulfilled') {
        let list = Array.isArray(tasksResult.value) ? tasksResult.value : [];
        const projectIds = (filters.projectIds || []).filter((id) => Number.isFinite(id) && id > 0);
        if (projectIds.length > 0) {
          list = list.filter((task) => projectIds.includes(task.project?.id || 0));
        }

        const hasAct =
          (filters.activityIds || []).some((id) => Number.isFinite(id) && id > 0) ||
          (filters.activityNumbers || []).some((n) => String(n || '').trim().length > 0);
        if (hasAct) {
          list = list.filter((task) =>
            taskMatchesScheduleActivityFilter(task, {
              activityIds: filters.activityIds || [],
              activityNumbers: filters.activityNumbers || [],
            })
          );
        }

        if (equipmentTypeNeedles.length > 0) {
          list = filterTasksByEquipmentTypeMapped(list, vehicleRows, equipmentTypeNeedles);
        }

        const statusFilter = (filters.statuses || []).map((s) => String(s || '').trim()).filter((s) => s.length > 0);
        if (statusFilter.length > 0) {
          list = list.filter((task) =>
            taskStatusMatchesScheduleSelection(String(task.status ?? '').trim(), statusFilter)
          );
        }

        list = list.filter((task) =>
          taskMatchesSchedulePlannedDateFilters(task, filters.startDate, filters.endDate)
        );
        list = list.filter(hasPlannedStartAndEnd);

        setTasksRaw(list);
        setLoadError(null);
      } else {
        console.error('Schedule task table: failed to load tasks', tasksResult.reason);
        setTasksRaw(null);
        const status = (tasksResult.reason as { response?: { status?: number } })?.response?.status;
        setLoadError(status === 403 ? 'forbidden' : 'error');
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    dateFrom,
    dateTo,
    filters.serviceAreas,
    filters.foremanAreas,
    filters.workTypes,
    filters.taskSearch,
    filters.projectIds,
    filters.activityIds,
    filters.activityNumbers,
    filters.statuses,
    filters.equipmentTypes,
    filters.startDate,
    filters.endDate,
    serviceAreaParam,
    foremanAreaParam,
    workTypeParam,
  ]);

  const tasksDisplayOrder = useMemo(() => orderScheduleTasksForDisplay(tasksRaw), [tasksRaw]);

  const scheduleOrderIndexByTaskId = useMemo(() => {
    const m = new Map<number, number>();
    (Array.isArray(tasksDisplayOrder) ? tasksDisplayOrder : []).forEach((t, i) => m.set(t.id, i));
    return m;
  }, [tasksDisplayOrder]);

  const flatRows: TaskRow[] = useMemo(() => {
    const list = Array.isArray(tasksDisplayOrder) ? tasksDisplayOrder : [];
    return list.map((task) => {
      const plannedStartRaw = String(task.identifiedDate ?? task.startDate ?? '').trim();
      const plannedEndRaw = String(task.plannedCompletionDate ?? '').trim();
      const proj = task.project;
      const projectLabel = proj
        ? proj.projectNumber
          ? `${proj.projectNumber} - ${proj.name}`
          : proj.name
        : '–';
      return {
        taskId: task.id,
        foremanAreaName: task.foremanArea?.name?.trim() || '–',
        serviceAreaName: task.serviceArea?.name?.trim() || '–',
        projectLabel,
        taskNumber: String(task.taskNumber ?? '').trim() || '–',
        taskName: String(task.name ?? '').trim() || '–',
        assigneesLabel: assigneesLabel(task.assignees),
        plannedStartRaw,
        plannedEndRaw,
        status: formatScheduleTaskStatusLabel(String(task.status ?? '')) || '–',
        yearRound: Boolean(task.yearRoundSchedule),
      };
    });
  }, [tasksDisplayOrder]);

  const dirMult = sortDir === 'asc' ? 1 : -1;

  const sortedRows = useMemo(() => {
    const slice = flatRows.slice();
    slice.sort((a, b) => {
      switch (sortKey) {
        case 'scheduleOrder': {
          const ia = scheduleOrderIndexByTaskId.get(a.taskId) ?? 0;
          const ib = scheduleOrderIndexByTaskId.get(b.taskId) ?? 0;
          return ia - ib;
        }
        case 'plannedStart':
          return compareNullableTime(
            dateStringToSortTime(a.plannedStartRaw),
            dateStringToSortTime(b.plannedStartRaw),
            dirMult
          );
        case 'plannedEnd':
          return compareNullableTime(
            dateStringToSortTime(a.plannedEndRaw),
            dateStringToSortTime(b.plannedEndRaw),
            dirMult
          );
        case 'yearRound':
          return dirMult * (Number(a.yearRound) - Number(b.yearRound));
        case 'foremanAreaName':
          return compareLocale(a.foremanAreaName, b.foremanAreaName, dirMult);
        case 'serviceAreaName':
          return compareLocale(a.serviceAreaName, b.serviceAreaName, dirMult);
        case 'taskNumber':
          return compareLocale(a.taskNumber, b.taskNumber, dirMult);
        case 'taskName':
          return compareLocale(a.taskName, b.taskName, dirMult);
        case 'assigneesLabel':
          return compareLocale(a.assigneesLabel, b.assigneesLabel, dirMult);
        case 'status':
          return compareLocale(a.status, b.status, dirMult);
        default:
          return 0;
      }
    });
    return slice;
  }, [flatRows, sortKey, sortDir, dirMult, scheduleOrderIndexByTaskId]);

  const totalCount = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const paginatedRows = useMemo(
    () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
    [sortedRows, page, pageSize]
  );

  const toggleSort = (key: TaskSortKey) => {
    if (key === 'scheduleOrder') {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    setSortDir((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const th = (key: TaskSortKey, label: string) => {
    const active = sortKey === key;
    // Standard app sort-arrow set (matches SortableTh / QP getSortIcon): faint up/down when
    // inactive, directional caret when active.
    const icon = !active ? (
      <i className="bi bi-arrow-down-up tw-ml-1 tw-text-content-muted" aria-hidden="true" />
    ) : sortDir === 'asc' ? (
      <i className="bi bi-sort-down-alt tw-ml-1" aria-hidden="true" />
    ) : (
      <i className="bi bi-sort-down tw-ml-1" aria-hidden="true" />
    );
    return (
      <th
        role="button"
        tabIndex={0}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        onClick={() => toggleSort(key)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleSort(key);
          }
        }}
      >
        {label}
        {icon}
      </th>
    );
  };

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-grow min-h-0 tw-py-5 tw-gap-2">
        <Spinner animation="border" size="sm" />
        <span className="tw-text-content-muted">Loading task schedule…</span>
      </div>
    );
  }

  if (loadError === 'forbidden') {
    return (
      <div className="tw-grow min-h-0">
        <Alert variant="warning">You do not have permission to view the schedule.</Alert>
      </div>
    );
  }

  if (loadError === 'error') {
    return (
      <div className="tw-grow min-h-0">
        <Alert variant="danger">Failed to load schedule tasks. Try again or adjust filters.</Alert>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-grow min-h-0" style={{ minHeight: 0 }}>
      <div className="tw-shrink-0 tw-text-sm tw-text-content-muted tw-mb-2">
        Showing tasks from {dateFrom} to {dateTo}.
      </div>
      {sortedRows.length === 0 ? (
        <div className="tw-text-content-muted tw-py-4 tw-shrink-0">No tasks match the current filters and date range.</div>
      ) : (
        <>
          {/* Phones land on this table (the Gantt needs a wider screen), so it gets a stacked
              card list instead of a 10-column horizontal scroll. Same data, same order. */}
          <div className="tw-grow min-h-0 tw-overflow-y-auto tw-flex tw-flex-col tw-gap-2 tw-pb-3 md:tw-hidden">
            {paginatedRows.map((r) => (
              <div
                key={`m-${r.taskId}`}
                className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3 tw-shadow-sm"
              >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                  <div className="tw-min-w-0">
                    {r.taskNumber !== '–' ? (
                      <Link
                        to={`/tasks/quantified?taskId=${r.taskId}`}
                        className="tw-font-semibold"
                        aria-label={`Open task ${r.taskNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {r.taskNumber}
                      </Link>
                    ) : (
                      <span className="tw-font-semibold tw-text-content">–</span>
                    )}
                    <div className="tw-mt-0.5 tw-break-words tw-text-sm tw-text-content">{r.taskName}</div>
                  </div>
                  <span className="tw-shrink-0 tw-rounded-full tw-bg-surface-alt tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-content-secondary">
                    {r.status}
                  </span>
                </div>
                <div className="tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1.5 tw-text-sm">
                  <div>
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Planned start</div>
                    <div className="tw-text-content">{formatScheduleDateCell(r.plannedStartRaw)}</div>
                  </div>
                  <div>
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Planned end</div>
                    <div className="tw-text-content">{formatScheduleDateCell(r.plannedEndRaw)}</div>
                  </div>
                  <div>
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Foreman area</div>
                    <div className="tw-break-words tw-text-content">{r.foremanAreaName}</div>
                  </div>
                  <div>
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Assignees</div>
                    <div className="tw-break-words tw-text-content">{r.assigneesLabel}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="tw-hidden tw-grow min-h-0 tw-overflow-auto tw-border tw-border-line tw-rounded tw-pb-3 md:tw-block">
            <div className="tw-overflow-x-auto">
              <Table striped bordered hover size="sm" className="tw-mb-0 tw-align-middle">
                <thead className="tw-bg-surface tw-sticky tw-top-0">
                  <tr>
                    {th('foremanAreaName', 'Foreman Area')}
                    {th('serviceAreaName', 'Service Area')}
                    {th('taskNumber', 'Task ID')}
                    {th('taskName', 'Task Name')}
                    {th('assigneesLabel', 'Assignees')}
                    {th('plannedStart', 'Planned Start')}
                    {th('plannedEnd', 'Planned End')}
                    {th('status', 'Status')}
                    {th('yearRound', 'Year-Round')}
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((r) => (
                    <tr key={r.taskId}>
                      <td>{r.foremanAreaName}</td>
                      <td><ServiceAreaPill value={r.serviceAreaName === '–' ? null : r.serviceAreaName} compact /></td>
                      <td>
                        {r.taskNumber !== '–' ? (
                          <Link
                            to={`/tasks/quantified?taskId=${r.taskId}`}
                            className="tw-text-sm"
                            aria-label={`Open task ${r.taskNumber}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {r.taskNumber}
                          </Link>
                        ) : (
                          '–'
                        )}
                      </td>
                      {/* V3-022 / TBL-06: at most 2 lines, full name in the shared tooltip. */}
                      <td><ClampText>{r.taskName}</ClampText></td>
                      <td>{r.assigneesLabel}</td>
                      <td className="tw-whitespace-nowrap">{formatScheduleDateCell(r.plannedStartRaw)}</td>
                      <td className="tw-whitespace-nowrap">{formatScheduleDateCell(r.plannedEndRaw)}</td>
                      <td>{r.status}</td>
                      <td>{r.yearRound ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
          <div className="tw-shrink-0 tw-pt-2">
            <ServerPagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ScheduleTaskTable;
